import type { FaultConfig, KpiConfig } from '../../engine/plant/types';

export const faults: FaultConfig[] = [
  {
    id: 'STORM',
    name: 'Storm turbidity spike',
    kind: 'sourceRamp',
    target: 'SRC-001',
    field: 'ntu',
    params: { delta: 140, overS: 3600 },
    description: 'Catchment runoff drives raw turbidity from 8 NTU toward 150 NTU over an hour.',
  },
  {
    id: 'STORM-FAST',
    name: 'Flash storm',
    kind: 'sourceRamp',
    target: 'SRC-001',
    field: 'ntu',
    params: { delta: 90, overS: 900 },
    description: 'A sharp 15-minute rise in raw turbidity.',
  },
  {
    id: 'COLD-SNAP',
    name: 'Cold water',
    kind: 'sourceRamp',
    target: 'SRC-001',
    field: 'temperature',
    params: { delta: -6, overS: 7200 },
    description: 'Raw water temperature falls 6 K — coagulation slows and floc quality drops.',
  },
  { id: 'P101A-TRIP', name: 'Intake pump A trip', kind: 'pumpTrip', target: 'P-101A', description: 'Motor protection trips intake pump A.' },
  { id: 'P101B-TRIP', name: 'Intake pump B trip', kind: 'pumpTrip', target: 'P-101B', description: 'Motor protection trips intake pump B.' },
  { id: 'P701A-TRIP', name: 'High-lift pump A trip', kind: 'pumpTrip', target: 'P-701A', description: 'Motor protection trips high-lift pump A.' },
  { id: 'PD201-FAIL', name: 'Coagulant pump failure', kind: 'pumpTrip', target: 'PD-201', description: 'The alum dosing pump stops delivering.' },
  { id: 'PD601-FAIL', name: 'Chlorine pump failure', kind: 'pumpTrip', target: 'PD-601', description: 'The chlorine dosing pump stops delivering.' },
  {
    id: 'AT601-DRIFT',
    name: 'Chlorine analyzer drifting low',
    kind: 'txDrift',
    target: 'AT-601',
    params: { pctPerHour: 4, sign: -1 },
    description:
      'The residual analyzer reads progressively low. AIC-601 will chase it and over-dose — the classic trap.',
  },
  { id: 'AT301-FREEZE', name: 'Settled turbidity analyzer frozen', kind: 'txFreeze', target: 'AT-301', description: 'The settled water turbidimeter holds its last value.' },
  { id: 'FT101-SPIKE', name: 'Raw flow transmitter spiking', kind: 'txSpike', target: 'FT-101', params: { perHour: 30, pctSpan: 25 }, description: 'Intermittent spikes on the raw water flow signal.' },
  { id: 'LT501-FAILHI', name: 'Clearwell level transmitter fail high', kind: 'txFailHigh', target: 'LT-501', description: 'The clearwell level transmitter drives to full scale.' },
  {
    id: 'FV402-STICTION',
    name: 'Filter 2 rate valve stiction',
    kind: 'valveStiction',
    target: 'FV-402',
    params: { S: 4, J: 2 },
    description: 'The rate-of-flow valve sticks and slips, producing a limit cycle on FIC-402.',
  },
  { id: 'IV403-STUCK', name: 'Filter 3 inlet valve stuck', kind: 'valveStuck', target: 'IV-403', params: { position: 1 }, description: 'The filter 3 inlet valve will not close.' },
  { id: 'F402-CLOG', name: 'Filter 2 media clogging', kind: 'clogging', target: 'F-402', params: { metres: 0.8 }, description: 'Mudballing adds fixed headloss that a backwash will not remove.' },
  { id: 'CW701-LEAK', name: 'Clearwell leak', kind: 'leak', target: 'CW-701', params: { m3h: 60 }, description: 'A 60 m³/h loss from the clearwell.' },
  { id: 'AIR-LOSS', name: 'Instrument air failure', kind: 'airLoss', target: '*', description: 'Every control valve strokes to its fail-safe position.' },
  { id: 'DEMAND-SURGE', name: 'Distribution demand surge', kind: 'sourceRamp', target: 'DIST', params: { delta: 220, overS: 900 }, description: 'A sudden 220 m³/h rise in distribution demand.' },
];

export const kpis: KpiConfig = {
  spec: [
    { tag: 'AT-405.PV', hi: 0.3, label: 'Final turbidity ≤ 0.3 NTU' },
    { tag: 'AT-601.PV', lo: 0.2, hi: 4.0, label: 'Chlorine residual 0.2–4.0 mg/L' },
    { tag: 'CT-601.CTRATIO', lo: 1.0, label: 'CT ratio ≥ 1.0' },
    { tag: 'AT-602.PV', lo: 7.0, hi: 8.6, label: 'pH 7.0–8.6' },
    { tag: 'PT-701.PV', lo: 3.5, label: 'Distribution pressure ≥ 3.5 barg' },
  ],
  throughput: { tag: 'S-DIST.Q', label: 'Water supplied', units: 'm³' },
  energy: [
    { tag: 'P-101A.KW', label: 'Intake pump A', units: 'kW' },
    { tag: 'P-101B.KW', label: 'Intake pump B', units: 'kW' },
    { tag: 'P-701A.KW', label: 'High-lift pump A', units: 'kW' },
    { tag: 'P-701B.KW', label: 'High-lift pump B', units: 'kW' },
  ],
  chemicals: [
    { tag: 'PD-201.TOTAL', label: 'Alum', units: 'kg' },
    { tag: 'PD-601.TOTAL', label: 'Chlorine', units: 'kg' },
    { tag: 'PD-602.TOTAL', label: 'Caustic', units: 'kg' },
  ],
  danger: [
    { id: 'turbidity', label: 'Final turbidity above the regulatory limit', cond: { op: '>', tag: 'AT-405.PV', value: 0.3 } },
    { id: 'ct', label: 'CT credit not met', cond: { op: '<', tag: 'CT-601.CTRATIO', value: 1.0 } },
    { id: 'residual', label: 'Residual below 0.2 mg/L', cond: { op: '<', tag: 'AT-601.PV', value: 0.2 } },
    { id: 'pressure', label: 'Distribution pressure below 3.5 barg', cond: { op: '<', tag: 'PT-701.PV', value: 3.5 } },
  ],
  loops: ['FIC-101', 'LIC-501', 'AIC-201', 'FIC-401', 'FIC-402', 'FIC-403', 'FIC-404', 'AIC-601', 'AIC-602', 'PIC-701'],
};
